import { WEBVIEW_EVENTS, RN_TO_WEBVIEW_EVENTS } from '../constant/constant';

export function sendPostMessage(type, data) {
	window.ReactNativeWebView?.postMessage(JSON.stringify({ type, ...data }));
}

export function navigateToScreen(name, params) {
	noteWebviewHandoff();
	sendPostMessage(WEBVIEW_EVENTS.NAVIGATION, {
		nav: {
			name,
			params,
		},
	});
}

export function goBack() {
	// Flush the leave BEFORE the handoff, while the bridge is still alive: the RN shell
	// destroys this WebView the instant it pops the screen, so a pagehide post fired
	// during teardown has nowhere to go (same reason stories/ flushes before goBack).
	flushWebviewLeave('goback');
	sendPostMessage(WEBVIEW_EVENTS.GO_BACK);
}

// Tell the native host whether this page wants to intercept the Android hardware
// back press. When `enabled` is true the host stops handling back itself and
// instead sends RN_TO_WEBVIEW_EVENTS.HARDWARE_BACK_PRESS into the WebView.
// Prefer the useHardwareBack() hook over calling this directly.
export function registerBackHandler(enabled) {
	sendPostMessage(WEBVIEW_EVENTS.REGISTER_BACK_HANDLER, { enabled: Boolean(enabled) });
}

export function pushToScreen(name, params) {
	noteWebviewHandoff();
	sendPostMessage(WEBVIEW_EVENTS.PUSH, {
		nav: {
			name,
			params,
		},
	});
}

export function openMediaUploader(data) {
	sendPostMessage(WEBVIEW_EVENTS.OPEN_GALLERY, {
		data,
	});
}

export function getCameraPermission() {
	sendPostMessage(WEBVIEW_EVENTS.GET_CAMERA_PERMISSION);
}

// Forward an analytics event to the native app's Mixpanel instance.
// Native handler: case 'mp-track' → mixpanel.track(eventName, properties).
export function mpTrack(eventName, properties = {}) {
	sendPostMessage(WEBVIEW_EVENTS.MP_TRACK, { eventName, properties });
}

// Report page-load milestones to the native shell, once per document load.
// The RN host (screens/webView.tsx) merges these into its 'WebView Loaded'
// Mixpanel event so native and page clocks land in one row. All values are
// ms since the page's navigation start; null means "not available yet /
// unsupported". No-ops outside the app WebView and never throws — telemetry
// must never break the page.
let webviewPerfReported = false;
export function reportWebviewPerf() {
	try {
		if (!window.ReactNativeWebView || webviewPerfReported) return;
		webviewPerfReported = true;
		const nav = performance.getEntriesByType?.('navigation')?.[0];
		const ms = (value) => (typeof value === 'number' && value > 0 ? Math.round(value) : null);
		sendPostMessage(WEBVIEW_EVENTS.WEBVIEW_PERF, {
			ttfb_ms: ms(nav?.responseStart),
			dom_interactive_ms: ms(nav?.domInteractive),
			dcl_ms: ms(nav?.domContentLoadedEventEnd),
			app_mounted_ms: ms(performance.now()),
		});
	} catch (e) {
		// Swallow — see note above.
	}
}

// Landing-timeline milestones. The clock and the 'Webview Opened' / 'Webview Left'
// events live in an inline script in index.html (window.__kcWv), because they have to
// fire for a page that never gets as far as this bundle. These helpers are how bundle
// code advances that timeline: mark() records the furthest stage reached, so that when
// the page is left, 'Webview Left' can say exactly where the buyer got to — html,
// bundle, mounted, landed, painted, failed. track() stamps an event with the shared
// ms_since_open clock. Both are no-ops outside the app WebView and never throw.
export function markWebviewMilestone(name) {
	try {
		window.__kcWv?.mark?.(name);
	} catch (e) {
		/* telemetry must never break the page */
	}
}

export function trackWebviewMilestone(eventName, properties = {}) {
	try {
		window.__kcWv?.track?.(eventName, properties);
	} catch (e) {
		/* telemetry must never break the page */
	}
}

// The page is sending the buyer to a native screen. The WebView survives underneath, so
// the 'hidden' that follows is not an exit; this count lets 'Webview Left' say so.
export function noteWebviewHandoff() {
	try {
		window.__kcWv?.handoff?.();
	} catch (e) {
		/* telemetry must never break the page */
	}
}

// Post 'Webview Left' now, while the bridge is alive. For paths where the shell tears
// the WebView down synchronously (go-back), waiting for pagehide is too late.
export function flushWebviewLeave(reason) {
	try {
		window.__kcWv?.left?.(reason);
	} catch (e) {
		/* telemetry must never break the page */
	}
}

// Report the moment the first content screen becomes usable (route data in,
// loader gone) to native Mixpanel as a standalone 'WebView Content Painted'
// event, once per document load. This is the "stage 3" beacon: web_app_mounted
// (in 'WebView Loaded') marks the React shell mounting; this marks real content
// on screen. content_painted_ms is ms since the page's navigation start — the
// SAME clock as web_app_mounted_ms / web_dcl_ms — so the two events line up on
// a dashboard by comparing medians, no per-event join needed. Goes over the
// existing mp-track bridge, so NO native change is required. No-ops outside the
// app WebView and never throws.
let webviewContentPaintedReported = false;
export function reportContentPainted(extra = {}) {
	try {
		// The milestone advances on EVERY screen paint, above the once-latch below. The
		// latch is for the event (one Content Painted per document); the milestone is for
		// the timeline, and a second screen painting must still count as painted.
		markWebviewMilestone('painted');
		if (!window.ReactNativeWebView || webviewContentPaintedReported) return;
		webviewContentPaintedReported = true;
		mpTrack('WebView Content Painted', {
			content_painted_ms: Math.round(performance.now()),
			path: window.location?.pathname,
			...extra,
		});
	} catch (e) {
		// Swallow — telemetry must never break the page.
	}
}

// Native mic recording bridge. The native AudioRecorderModule records via
// Android MediaRecorder and posts the result back on RN_TO_WEBVIEW_EVENTS
// .AUDIO_RECORDING_*. `qid` is echoed back so a caller recording several clips
// can match each result to the right field. Prefer the useNativeAudioRecorder()
// hook over calling these directly.
export function startAudioRecording(qid) {
	sendPostMessage(WEBVIEW_EVENTS.START_AUDIO_RECORDING, { qid });
}

export function stopAudioRecording(qid) {
	sendPostMessage(WEBVIEW_EVENTS.STOP_AUDIO_RECORDING, { qid });
}

export function cancelAudioRecording(qid) {
	sendPostMessage(WEBVIEW_EVENTS.CANCEL_AUDIO_RECORDING, { qid });
}
